import {Inject, Injectable} from "@angular/core";
import {
    ActionService,
    collection,
    component,
    ConfigurationService,
    dateField,
    EntityData,
    field,
    identifier,
    JwtHttpClientService,
    ResourceModule
} from "@fedelta-pos/data-client";
import {OrderTransferTargetReferenceData} from "../group-order/order-transfer-target-entity.model";
import {OrderPaymentDetailsData} from "../payment/order-payment-details-entity.model";
import {AddressData} from "../customer-info/address.data";

import {DisplayableOrderExtraItem, DisplayableOrderItem, OrderType} from "./order";
import {StoreData} from "../store/store-search.model";
import {DealData} from "../deal/deal-entity.model";

export class ExtraItemData extends EntityData<ExtraItemData> implements DisplayableOrderExtraItem {
    @field sellableId: number;
    @field name: string;
    @field quantity: number;
    @field unitPrice: number;
    @field isGroup: boolean;
    @field isVisible: boolean;
    @field isNested: boolean;
    @field groupName: string;

    public get total(): number {
        return this.quantity * this.unitPrice;
    }
}

export class OrderItemData extends EntityData<OrderItemData> implements DisplayableOrderItem {
    @field identifier: string;
    @field sellableId: number;
    @field name: string;
    @field quantity: number;
    @field unitPrice: number;
    @field courseId: number;
    @field applyMinimumAge: boolean;
    @field reorderableInApp: boolean;

    @collection(ExtraItemData) extras: ExtraItemData[];

    @collection(OrderItemData) compositeOrderItems: OrderItemData[];

    // Populated once the order is initiated - required here for local persistence and display of recent orders
    @field initiatedTotal: number;
    @field initiatedExtrasTotal: number;

    @field transferRequestId: string;
    @field transferRequestName: string;

    // Not used on server - required here for local persistence and display of recent orders
    @field dealId: string;
    @field proposed: boolean;

    public get localTotal(): number {
        let totalExtras: number = 0;
        if (this.extras) {
            this.extras.forEach((orderExtraItem: ExtraItemData) => totalExtras += orderExtraItem.total);
        }
        return (this.quantity * this.unitPrice) + (this.quantity * totalExtras);
    }

    public get orderedExtras(): ExtraItemData[] {
        return this.extras;
    }
}

export class OrderDealData extends EntityData<OrderDealData> {
    @identifier id: string;
    @field integratedDealConfigId: number;
    @field providerName: string;

    public static fromDeal(deal: DealData): OrderDealData {
        const orderDeal: OrderDealData = <OrderDealData> EntityData.create(OrderDealData);
        orderDeal.id = deal.id;
        orderDeal.integratedDealConfigId = deal.integratedDealConfigId;
        orderDeal.providerName = deal.providerName;
        return orderDeal;
    }
}

export class OrderInitiationData extends EntityData<OrderInitiationData> {
    @field requestId: string;
    @field orderType: string;
    @field expectedCompletion: string;
    @field deliveryIdentifier: string;
    @field terminalName: string;
    @field email: string; // used for member lookup
    @field mobilePhone: string; // used for member lookup`
    @field orderPhoneNumber: string;
    @field firstName: string;
    @field lastName: string;
    @field tableNumber: string;
    @field beaconId: string;
    @field customerRefNo: string; // unused - set on server-side for unique order reference
    @collection(OrderDealData) deals: OrderDealData[];
    @field disableMixAndMatch: boolean;
    @collection(OrderItemData) items: OrderItemData[];
    @field tipAmount: number;
    @component address: AddressData;
    @field deliveryInstructions: string;

    /**
     * Indicates that date/time errors should be reported in the response message instead of failing the initiation.
     * Note that time errors will still need to be resolved before placing an order
     * @see InitiatedOrderData#unavailabilityReason
     */
    @field softTimeValidation: boolean = true;

    /**
     * Indicates that payment details should be returned if required by the platform
     */
    @field forPayment: boolean = false;
    @field platformCustomerId: string;

    /**
     * Indicates that integrated delivery times should be checked (required only during checkout)
     */
    @field checkIntegratedDeliveryTime: boolean = false;

    /**
     * Indicates the transfer target (group order) id currently 'owned' by this order.
     * For an order initiation request: used to exclude this target from the list of potential transfer targets.
     * For an order transfer request: used to remove the transfer target.
     */
    @field orderTransferTargetId: string;

    /**
     * For an order transfer request: indicates the transfer target id to which the order is being transferred.
     */
    @field transferTargetId: string;

    /**
     * For an order initiation request: persisted locally with the order for recovery (not used on the server).
     * For an order transfer request: indicates the transfer target name. Note that this is not used for matching, but only for user messaging.
     */
    @field transferTargetName: string;

    public get orderTypeEnum(): OrderType {
        return OrderType.parse(this.orderType);
    }

    public set orderTypeEnum(orderType: OrderType) {
        this.orderType = OrderType.toString(orderType);
    }

    public get totalItems(): number {
        return this.items.reduce((total: number, item: OrderItemData) => total + item.quantity, 0);
    }

    public get totalTransfers(): number {
        return this.items
            .filter((item: OrderItemData) => item.transferRequestId)
            .map((item: OrderItemData) => item.transferRequestId)
            .filter((transferRequestId: string, index: number, list: string[]) => list.indexOf(transferRequestId) === index)
            .length;
    }

    public getItemsForTransferRequest(transferRequestId: string): OrderItemData[] {
        return this.items.filter((item: OrderItemData) => item.transferRequestId == transferRequestId);
    }

    public getSubtotalForTransferRequest(transferRequestId: string): number {
        return this.items.filter((item: OrderItemData) => item.transferRequestId == transferRequestId).reduce((total: number, item: OrderItemData) => total + item.initiatedTotal, 0);
    }
}

export class PaymentSurchargeData extends EntityData<PaymentSurchargeData> {
    public static readonly CREDIT_CARD_TYPE: string = "CREDIT_CARD";

    @field type: string;
    @field amount: number;
    @field percentValue: number;
}

export class InitiatedOrderItemData extends EntityData<InitiatedOrderItemData> {
    @field identifier: string;
    @field total: number;
    @field extrasTotal: number;
    @collection(InitiatedOrderItemData) compositeOrderItems: InitiatedOrderItemData[];
}

export class OrderDiscountData extends EntityData<OrderDiscountData> {
    @field name: string;
    @field amount: number;
}

export class OrderSurchargeData extends EntityData<OrderSurchargeData> {
    @field name: string;
    @field amount: number;
}

export class InitiatedOrderData extends EntityData<InitiatedOrderData> {
    @field requestId: string;
    @field orderTotal: number;
    @field undiscountedOrderTotal: number;
    @field tipAmount: number;
    @collection(InitiatedOrderItemData) initiatedOrderItems: InitiatedOrderItemData[];
    @collection() usedDeals: string[];
    @field points: number;
    @field bonusPoints: number;
    @field pointsRedeemable: number;
    @field memberPointsBalance: number;
    @field memberAvailableRedemptionPoints: number;
    @field memberAvailableRedemptionCurrencyValue: number;
    @collection(OrderDiscountData) discounts: OrderDiscountData[];
    @field totalDiscounts: number;
    @collection(OrderSurchargeData) surcharges: OrderSurchargeData[];
    @field totalSurcharges: number;
    @field taxIncluded: number;
    @field taxExcluded: number;
    @dateField initiatedDateTime: Date;
    @dateField orderLaterDateTime: Date;

    /**
     * Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss
     */
    @field orderLaterLocalDateTime: string;

    /**
     * Estimated pickup time of the order (for delivery) in Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss
     */
    @field estimatedPickupLocalDateTime: string;

    /**
     * Estimated completion time of the order in Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss
     */
    @field estimatedCompletionLocalDateTime: string;

    /**
     * Earliest date that the order can be fulfilled in the format of yyyy-MM-dd
     */
    @field earliestLocalDate: string;

    @field cannotAllocateOrderLaterDateTime: boolean;
    @field unavailabilityReason: string;
    @field orderToken: string;
    @component store: StoreData;
    @collection(PaymentSurchargeData) paymentSurcharges: PaymentSurchargeData[];
    @collection(OrderTransferTargetReferenceData) availableTransferTargets: OrderTransferTargetReferenceData[];
    @component paymentDetails: OrderPaymentDetailsData;

    public getPaymentSurcharge(paymentType: string): PaymentSurchargeData {
        return this.orderTotal <= 0 ? undefined : this.paymentSurcharges.find((paymentSurcharge) => paymentSurcharge.type === paymentType);
    }

    public getOrderOrderTotalWithPaymentSurcharge(paymentType: string): number {
        const paymentSurcharge: PaymentSurchargeData = this.getPaymentSurcharge(paymentType);
        return this.orderTotal + (paymentSurcharge ? paymentSurcharge.amount : 0);
    }
}

@Injectable()
export class OrderInitiationService extends ActionService {
    constructor(http: JwtHttpClientService, configService: ConfigurationService, @Inject(ResourceModule.RESOURCE_NAMESPACE_KEY) nameSpace, @Inject(ResourceModule.ACTION_NAMES_KEY) actionNames) {
        console.log("--- OrderInitiationService");

        super(http, configService, nameSpace, actionNames, "web-order-initiation");
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order-initiation.service.ts